import React, { useState, useEffect } from 'react';
// import UnsignedArtistTable from '../components/streaming/UnsignedArtistTable';
import StreamingTable from '../components/streaming/StreamingTableV2';
import { XMarkIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import MetricsChart from '../components/shared/MetricsChart';
import { useToast } from '../components/shared/Toast';
import AddAlbumModal from '../components/shared/AddAlbumModal';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import SnoopSummaryPage from './SnoopSummaryPage';
import LuminateSummaryPage from './LuminateSummaryPage';
import LuminateArtistsSummaryPage from './LuminateArtistsSummaryPage';
import DealsPage from './DealsPage';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/streaming';

const TABS = [
  { id: 'songs', label: 'Songs' },
  { id: 'albums', label: 'Albums' },
  { id: 'artists', label: 'Artists' },
  { id: 'artist-aggregated', label: 'Artist Aggregated' },
  { id: 'agenda', label: 'Agenda' }
];

// Top-level data source for the Unsigned page. 'streaming' is the default and
// keeps the existing Songs/Albums/Artists tabs; the snoop views replace the
// whole body with the ported Snoop dashboards.
// Snoop Mapping manages which accounts get tracked, so it is admin-only.
// Discovery views, matching the coworker dashboard's "unsigned" suite nav.
// Mapping views (Snoop / Luminate / P-Line) live under the Mapping tab instead.
// Luminate Agg is intentionally absent — hidden upstream as "not ready for
// regular use" — but reachable at /luminate?view=agg.
const SOURCES = [
  { id: 'streaming', label: 'Streaming' },
  { id: 'snoop', label: 'Snoop' },
  { id: 'luminate', label: 'Luminate Tracks' },
  { id: 'luminate-artists', label: 'Luminate Artists' },
  { id: 'deals', label: 'Deals' }
];

// Rendered when the selector is on anything other than Streaming.
const SOURCE_VIEWS = {
  snoop: <SnoopSummaryPage />,
  luminate: <LuminateSummaryPage />,
  'luminate-artists': <LuminateArtistsSummaryPage />,
  deals: <DealsPage />
};

// function formatNumber(num) {
//   return new Intl.NumberFormat().format(num);
// }

// function formatPercent(num, showPlus = false) {
//   if (num === null || num === undefined) return '';
//   return `${showPlus && num > 0 ? '+' : ''}${num.toFixed(1)}%`;
// }

// function getPercentChangeColor(value) {
//   if (!value || value === 0) return 'text-gray-900';
//   return value > 0 ? 'text-green-600' : 'text-red-600';
// }

// function getTrackingLabelColor(label) {
//   if (!label) return 'bg-gray-100 text-gray-800';
  
//   switch (label.toLowerCase()) {
//     case 'high priority':
//       return 'bg-red-100 text-red-800';
//     case 'medium priority':
//       return 'bg-yellow-100 text-yellow-800';
//     case 'low priority':
//       return 'bg-green-100 text-green-800';
//     case 'tracking':
//       return 'bg-blue-100 text-blue-800';
//     case 'archive':
//       return 'bg-gray-200 text-gray-700';
//     default:
//       return 'bg-gray-100 text-gray-800';
//   }
// }

export default function UnsignedPageV2() {
  const { user } = useAuth();
  // "My Dashboard": when on, every tab is filtered to rows the current user is
  // the Set By for. Backed by the ?me=true URL param, so it's shareable /
  // bookmarkable and persists across tab switches.
  const [searchParams, setSearchParams] = useSearchParams();
  const myDashboard = searchParams.get('me') === 'true';
  const setMyDashboard = (next) => {
    const on = typeof next === 'function' ? next(myDashboard) : next;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (on) p.set('me', 'true');
      else p.delete('me');
      return p;
    }, { replace: true });
  };
  const forcedTrackedBy = myDashboard && user?.id ? String(user.id) : null;

  // Which dataset the page is showing. Backed by ?source= so it's shareable and
  // survives a reload; defaults to the existing streaming view.
  // Admin-only sources are dropped for everyone else, so a shared ?source= link
  // falls back to streaming rather than rendering a view they can't load.
  const availableSources = SOURCES.filter((s) => !s.adminOnly || user?.isAdmin);
  const sourceParam = searchParams.get('source');
  const activeSource = availableSources.some((s) => s.id === sourceParam)
    ? sourceParam
    : 'streaming';
  const setActiveSource = (next) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next && next !== 'streaming') p.set('source', next);
      else p.delete('source');
      return p;
    }, { replace: true });
  };

  const [activeTab, setActiveTab] = useState('songs');
  const [showAddModal, setShowAddModal] = useState(false);
  // Priority chosen in the add flows. '' = default (server inherits the artist's
  // existing label, or Tracking for a new artist). A non-empty value cascades to
  // the artist's other rows (label only) on the server.
  const [addSongLabel, setAddSongLabel] = useState('');
  const [addArtistLabel, setAddArtistLabel] = useState('');
  const [addMode, setAddMode] = useState(null); // 'id' or 'search'
  const [searchStep, setSearchStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [songIdInput, setSongIdInput] = useState('');
  const [expandedSongRow, setExpandedSongRow] = useState(null);
  const [expandedArtistRow, setExpandedArtistRow] = useState(null);
  const [expandedAggregatedRow, setExpandedAggregatedRow] = useState(null);
  const [expandedAgendaRow, setExpandedAgendaRow] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'last_complete_week_streams', order: 'desc' });
  const [selectedMetrics, setSelectedMetrics] = useState(['audio']);
  const [selectedRegion, setSelectedRegion] = useState('global');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('songs');
  // Agenda-tab export: chooser modal (HTML vs Excel), plus the HTML preview modal.
  const [showAgendaExportModal, setShowAgendaExportModal] = useState(false);
  const [agendaHtml, setAgendaHtml] = useState(null); // { html, plain } | null
  const [agendaHtmlCopied, setAgendaHtmlCopied] = useState(false);
  const [editingTrackingLabel, setEditingTrackingLabel] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);
  // const [audioOnly, setAudioOnly] = useState(false);
  const [lagDays, setLagDays] = useState('2'); // Default to 2-day lag
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [maxDates, setMaxDates] = useState({ weekly_date: null, daily_date: null });

  // Add Artist Modal States
  const [showAddArtistModal, setShowAddArtistModal] = useState(false);
  const [addArtistMode, setAddArtistMode] = useState(null); // 'id' or 'search'
  const [addArtistStep, setAddArtistStep] = useState(1);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistIdInput, setArtistIdInput] = useState('');
  const [artistNameInput, setArtistNameInput] = useState('');

  // Add Album Modal States
  const [showAddAlbumModal, setShowAddAlbumModal] = useState(false);
  const [expandedAlbumRow, setExpandedAlbumRow] = useState(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  const queryClient = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    const fetchMaxDates = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/max-dates`, {
          params: {
            is_pgp: false,
            group: 'Unsigned'
          }
        });
        setMaxDates(response.data);
      } catch (error) {
        console.error('Error fetching max dates:', error);
      }
    };

    fetchMaxDates();
  }, []);

  // Tracking label options
  const trackingLabelOptions = [
    { value: 'None', label: 'None' },
    { value: 'Tracking', label: 'Tracking' },
    { value: 'Archive', label: 'Archive' },
    { value: 'Agenda - Legal', label: 'Agenda - Legal' },
    { value: 'Agenda - Priorities', label: 'Agenda - Priorities' },
    { value: 'Agenda - New', label: 'Agenda - New' },
    { value: 'Agenda - Tracking', label: 'Agenda - Tracking' },
    { value: 'Destroy', label: 'Destroy' }
  ];

  // Options for the add-flow Priority picker. First option = default (server
  // inherits the artist's existing label, or Tracking for a new artist). We omit
  // None/Destroy since those aren't meaningful when adding to the tracked list.
  const addPriorityOptions = [
    { value: '', label: 'Default (keep existing / Tracking)' },
    ...trackingLabelOptions.filter(o => !['None', 'Destroy'].includes(o.value)),
  ];

  // Reusable Priority dropdown for the add modals.
  const renderAddPrioritySelect = (value, onChange) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
      >
        {addPriorityOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  // Metric options
  const metricOptions = [
    { id: 'total', label: 'Total Streams' },
    { id: 'audio', label: 'Audio Streams' },
    { id: 'video', label: 'Video Streams' },
    { id: 'ad', label: 'Ad-Supported' },
    { id: 'premium', label: 'Premium' },
    { id: 'ondemand', label: 'On-Demand' },
    { id: 'programmed', label: 'Programmed' }
  ];

  // Changing a tracking label (or "updated by") cascades the "updated by"
  // attribution to every sibling song/album/artist for the same artist name,
  // so any such change refreshes ALL three tabs.
  const invalidateAllTracked = () => {
    queryClient.invalidateQueries(['unsigned-songs']);
    queryClient.invalidateQueries(['unsigned-albums']);
    queryClient.invalidateQueries(['unsigned-artists']);
    queryClient.invalidateQueries(['unsigned-artist-aggregated']);
  };

  // Mutation for updating tracking label
  const updateTrackingLabelMutation = useMutation(
    async ({ songId, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/update-song-tracking-label/${songId}`, {
        tracking_label: trackingLabel
      });
      return response.data;
    },
    {
      onSuccess: invalidateAllTracked,
      onError: (error) => {
        console.error('Error updating tracking label:', error);
        alert('Error updating tracking label. Please try again.');
      }
    }
  );

  const updateArtistTrackingLabelMutation = useMutation(
    async ({ artistId, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/update-tracking-label/${artistId}`, {  // Changed from update-artist-tracking-label to update-tracking-label
        tracking_label: trackingLabel
      });
      return response.data;
    },
    {
      onSuccess: invalidateAllTracked,
      onError: (error) => {
        console.error('Error updating artist tracking label:', error);
        alert('Error updating tracking label. Please try again.');
      }
    }
  );

  const updateAlbumTrackingLabelMutation = useMutation(
    async ({ albumId, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/update-album-tracking-label/${albumId}`, {
        tracking_label: trackingLabel
      });
      return response.data;
    },
    {
      onSuccess: invalidateAllTracked,
      onError: (error) => {
        console.error('Error updating album tracking label:', error);
        alert('Error updating tracking label. Please try again.');
      }
    }
  );

  // Users who can be recorded as "updated by" (unsigned-access users).
  const { data: unsignedUsers } = useQuery({
    queryKey: ['unsigned-users'],
    queryFn: () => axios.get(`${API_BASE_URL}/unsigned-users`).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  // Mutations to manually set the "updated by" user, per record type.
  const onUpdatedByError = (error) => {
    console.error('Error updating "updated by":', error);
    alert('Error updating "updated by". Please try again.');
  };
  const updateSongUpdatedByMutation = useMutation(
    async ({ id, userId }) => (await axios.put(`${API_BASE_URL}/update-song-updated-by/${id}`, { user_id: userId })).data,
    { onSuccess: invalidateAllTracked, onError: onUpdatedByError }
  );
  const updateAlbumUpdatedByMutation = useMutation(
    async ({ id, userId }) => (await axios.put(`${API_BASE_URL}/update-album-updated-by/${id}`, { user_id: userId })).data,
    { onSuccess: invalidateAllTracked, onError: onUpdatedByError }
  );
  const updateArtistUpdatedByMutation = useMutation(
    async ({ id, userId }) => (await axios.put(`${API_BASE_URL}/update-artist-updated-by/${id}`, { user_id: userId })).data,
    { onSuccess: invalidateAllTracked, onError: onUpdatedByError }
  );

  const handleUpdatedByChange = (id, userId, type) => {
    if (type === 'artist') updateArtistUpdatedByMutation.mutate({ id, userId });
    else if (type === 'album') updateAlbumUpdatedByMutation.mutate({ id, userId });
    else updateSongUpdatedByMutation.mutate({ id, userId });
  };

  // Aggregated tab: set "updated by" by artist NAME (no single record) — this
  // cascades to all siblings, so refresh the aggregated tab + all three.
  const updateAggregatedUpdatedByMutation = useMutation(
    async ({ artistName, userId }) =>
      (await axios.put(`${API_BASE_URL}/update-artist-aggregated-updated-by`, { artist_name: artistName, user_id: userId })).data,
    {
      onSuccess: () => { invalidateAllTracked(); queryClient.invalidateQueries(['unsigned-artist-aggregated']); },
      onError: onUpdatedByError,
    }
  );
  const handleAggregatedUpdatedByChange = (artistName, userId) =>
    updateAggregatedUpdatedByMutation.mutate({ artistName, userId });

  // Agenda tab: rows are per-artist (id === artist name). Priority + Set By
  // edits cascade by name to every sibling song/album/artist, so refresh all
  // tabs including Agenda.
  const invalidateAgenda = () => { invalidateAllTracked(); queryClient.invalidateQueries(['unsigned-agenda']); };

  const updateAgendaTrackingLabelMutation = useMutation(
    async ({ artistName, trackingLabel }) =>
      (await axios.put(`${API_BASE_URL}/update-artist-aggregated-tracking-label`, { artist_name: artistName, tracking_label: trackingLabel })).data,
    {
      onSuccess: invalidateAgenda,
      onError: (error) => { console.error('Error updating tracking label:', error); alert('Error updating tracking label. Please try again.'); },
    }
  );
  const handleAgendaTrackingLabelChange = (artistName, newLabel) =>
    updateAgendaTrackingLabelMutation.mutate({ artistName, trackingLabel: newLabel || null });

  const updateAgendaUpdatedByMutation = useMutation(
    async ({ artistName, userId }) =>
      (await axios.put(`${API_BASE_URL}/update-artist-aggregated-updated-by`, { artist_name: artistName, user_id: userId })).data,
    {
      onSuccess: invalidateAgenda,
      onError: onUpdatedByError,
    }
  );
  const handleAgendaUpdatedByChange = (artistName, userId) =>
    updateAgendaUpdatedByMutation.mutate({ artistName, userId });

  // Per-tab column filters and row selection. Selection is cleared whenever
  // filters, sort, or the tab itself changes, so a pending bulk action cannot
  // accidentally apply to rows the user can no longer see.
  const [songColumnFilters, setSongColumnFilters] = useState({});
  const [albumColumnFilters, setAlbumColumnFilters] = useState({});
  const [artistColumnFilters, setArtistColumnFilters] = useState({});
  const [aggregatedColumnFilters, setAggregatedColumnFilters] = useState({});
  const [agendaColumnFilters, setAgendaColumnFilters] = useState({});
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState([]);
  const [selectedArtistIds, setSelectedArtistIds] = useState([]);
  const [bulkLabel, setBulkLabel] = useState('');
  // Change-status mode hides checkboxes by default; users opt in via the button.
  const [changeStatusMode, setChangeStatusMode] = useState(false);

  useEffect(() => {
    setSelectedSongIds([]);
    setSelectedAlbumIds([]);
    setSelectedArtistIds([]);
    setChangeStatusMode(false);
  }, [activeTab, sortConfig, showArchived, lagDays, songColumnFilters, albumColumnFilters, artistColumnFilters, aggregatedColumnFilters]);

  // The Agenda tab defaults to the Agenda-label ordering (Legal > Priorities >
  // New > Tracking, then building week streams desc). Leaving the tab restores
  // the standard streams sort. User clicks on a column header still override it.
  useEffect(() => {
    if (activeTab === 'agenda') {
      setSortConfig({ key: 'agenda_label', order: 'asc' });
    } else if (sortConfig.key === 'agenda_label') {
      setSortConfig({ key: 'last_complete_week_streams', order: 'desc' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const bulkUpdateSongLabelMutation = useMutation(
    async ({ ids, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/bulk-update-song-tracking-labels`, {
        ids,
        tracking_label: trackingLabel,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['unsigned-songs']);
        setSelectedSongIds([]);
        toast.success('Bulk label applied', `Updated ${data?.updated ?? 0} song${(data?.updated ?? 0) === 1 ? '' : 's'}`);
      },
      onError: () => alert('Error applying bulk label. Please try again.'),
    }
  );

  const bulkUpdateAlbumLabelMutation = useMutation(
    async ({ ids, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/bulk-update-album-tracking-labels`, {
        ids,
        tracking_label: trackingLabel,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['unsigned-albums']);
        setSelectedAlbumIds([]);
        toast.success('Bulk label applied', `Updated ${data?.updated ?? 0} album${(data?.updated ?? 0) === 1 ? '' : 's'}`);
      },
      onError: () => alert('Error applying bulk label. Please try again.'),
    }
  );

  const bulkUpdateArtistLabelMutation = useMutation(
    async ({ ids, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/bulk-update-artist-tracking-labels`, {
        ids,
        tracking_label: trackingLabel,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['unsigned-artists']);
        setSelectedArtistIds([]);
        toast.success('Bulk label applied', `Updated ${data?.updated ?? 0} artist${(data?.updated ?? 0) === 1 ? '' : 's'}`);
      },
      onError: () => alert('Error applying bulk label. Please try again.'),
    }
  );

  const currentSelection = activeTab === 'songs' ? selectedSongIds
    : activeTab === 'albums' ? selectedAlbumIds
    : activeTab === 'artists' ? selectedArtistIds
    : [];

  const clearCurrentSelection = () => {
    if (activeTab === 'songs') setSelectedSongIds([]);
    else if (activeTab === 'albums') setSelectedAlbumIds([]);
    else if (activeTab === 'artists') setSelectedArtistIds([]);
  };

  const applyBulkLabel = () => {
    if (!currentSelection.length || !bulkLabel) return;
    const trackingLabel = bulkLabel === 'None' ? null : bulkLabel;
    const confirmed = window.confirm(
      `Apply label "${bulkLabel}" to ${currentSelection.length} ${activeTab}?`
    );
    if (!confirmed) return;
    if (activeTab === 'songs') {
      bulkUpdateSongLabelMutation.mutate({ ids: currentSelection, trackingLabel });
    } else if (activeTab === 'albums') {
      bulkUpdateAlbumLabelMutation.mutate({ ids: currentSelection, trackingLabel });
    } else if (activeTab === 'artists') {
      bulkUpdateArtistLabelMutation.mutate({ ids: currentSelection, trackingLabel });
    }
  };

  // Modify the handleTrackingLabelChange function to handle all types
  const handleTrackingLabelChange = (id, newLabel, type) => {
    if (type === 'artist') {
      updateArtistTrackingLabelMutation.mutate({
        artistId: id,
        trackingLabel: newLabel || null
      });
    } else if (type === 'album') {
      updateAlbumTrackingLabelMutation.mutate({
        albumId: id,
        trackingLabel: newLabel || null
      });
    } else {
      updateTrackingLabelMutation.mutate({
        songId: id,
        trackingLabel: newLabel || null
      });
    }
  };

  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  // Get the label of the first selected metric for display
  const selectedMetricLabel = metricOptions.find(opt => selectedMetrics.includes(opt.id))?.label || 'Select Type';

  // Debounce song filter
  // useEffect(() => {
  //   const timer = setTimeout(() => setDebouncedFilter(songFilter), 500);
  //   return () => clearTimeout(timer);
  // }, [songFilter]);

  // Debounce artist filter
  // useEffect(() => {
  //   const timer = setTimeout(() => setDebouncedArtistFilter(artistFilter), 500);
  //   return () => clearTimeout(timer);
  // }, [artistFilter]);

  const handleSort = (key, explicitOrder = null) => {
    setSortConfig(prev => ({
      key,
      order: explicitOrder || (prev.key === key && prev.order === 'desc' ? 'asc' : 'desc')
    }));
  };

  const handleSongRowClick = (item) => {
    setExpandedSongRow(expandedSongRow === item.id ? null : item.id);
  };

  const handleArtistRowClick = (item) => {
    setExpandedArtistRow(expandedArtistRow === item.id ? null : item.id);
  };

  const handleAggregatedRowClick = (item) => {
    setExpandedAggregatedRow(expandedAggregatedRow === item.id ? null : item.id);
  };

  const handleAgendaRowClick = (item) => {
    setExpandedAgendaRow(expandedAgendaRow === item.id ? null : item.id);
  };

  const handleAlbumRowClick = (item) => {
    setExpandedAlbumRow(expandedAlbumRow === item.id ? null : item.id);
  };

//   const SortIcon = ({ columnKey }) => {
//     if (sortConfig.key !== columnKey) return null;
//     return sortConfig.order === 'desc' ? 
//       <ArrowDownIcon className="h-4 w-4" /> : 
//       <ArrowUpIcon className="h-4 w-4" />;
//   };

  // Intersection Observer for infinite scroll
  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     entries => {
  //       if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
  //         fetchNextPage();
  //       }
  //     },
  //     { threshold: 1.0 }
  //   );

  //   if (observerTarget.current) {
  //     observer.observe(observerTarget.current);
  //   }

  //   return () => observer.disconnect();
  // }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // const allData = data?.pages.flatMap(page => page.data) || [];

  const handleExport = async (format) => {
    try {
      let endpoint, params;
      if (exportType === 'songs') {
        endpoint = 'song-export';
        params = {
          format,
          // audio_only: audioOnly,
          show_archived: showArchived,
          group: 'Unsigned',
          sort_by: sortConfig.key,
          sort_order: sortConfig.order
        };
      } else {
        endpoint = 'unsigned-artist-export';
        params = {
          format,
          // audio_only: audioOnly,
          show_archived: showArchived
        };
      }
      
      const response = await axios.get(`${API_BASE_URL}/${endpoint}`, {
        responseType: 'blob',
        params: params
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileExtension = format === 'csv' ? 'csv' : 'pdf';
      const prefix = exportType === 'songs' ? 'unsigned_songs' : 'unsigned_artists';
      link.setAttribute('download', `${prefix}_${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  // Export all tabs to Excel with multiple sheets
  const handleExportExcel = async () => {
    setIsExporting(true);
    const toastId = toast.loading('Exporting...', 'Fetching data from all tabs');

    try {
      // Fetch all data from each tab (with high per_page to get all records)
      const [songsRes, albumsRes, artistsRes, aggregatedRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/song-summaries`, {
          params: { group: 'Unsigned', show_archived: showArchived, per_page: 10000, lag_days: lagDays }
        }),
        axios.get(`${API_BASE_URL}/unsigned-album-summaries`, {
          params: { show_archived: showArchived, per_page: 10000, lag_days: lagDays }
        }),
        axios.get(`${API_BASE_URL}/unsigned-artist-summaries`, {
          params: { show_archived: showArchived, per_page: 10000, lag_days: lagDays }
        }),
        axios.get(`${API_BASE_URL}/unsigned-artist-aggregated`, {
          params: { show_archived: showArchived, per_page: 10000, lag_days: lagDays }
        })
      ]);

      // Format data for each sheet
      const formatNumber = (num) => num !== null && num !== undefined ? Number(num) : 0;
      const formatPercent = (num) => num !== null && num !== undefined ? Number(num.toFixed(2)) : 0;

      // Songs sheet
      const songsData = (songsRes.data.data || []).map(song => ({
        'Artist': song.artist || '',
        'Song': song.title || '',
        'Priority': song.tracking_label || '',
        'Global Building': formatNumber(song.global_building_week_streams),
        'Global Building %': formatPercent(song.global_building_week_change_percent),
        'Global Last Week': formatNumber(song.global_last_complete_week_streams),
        'Global % Change': formatPercent(song.global_week_over_week_change_percent),
        'US %': formatPercent(song.us_streams_percent),
        'First Friday': song.first_friday_date || '',
        'First Friday Global': formatNumber(song.first_friday_global_streams),
        'First Friday US': formatNumber(song.first_friday_us_streams),
        'First Week Global': formatNumber(song.first_week_global_streams),
        'First Week US': formatNumber(song.first_week_us_streams)
      }));

      // Albums sheet
      const albumsData = (albumsRes.data.data || []).map(album => ({
        'Artist': album.artist || '',
        'Album': album.title || '',
        'Priority': album.tracking_label || '',
        'Global Building': formatNumber(album.global_building_week_streams),
        'Global Building %': formatPercent(album.global_building_week_change_percent),
        'Global Last Week': formatNumber(album.global_last_complete_week_streams),
        'Global % Change': formatPercent(album.global_week_over_week_change_percent),
        'US %': formatPercent(album.us_streams_percent)
      }));

      // Artists sheet
      const artistsData = (artistsRes.data.data || []).map(artist => ({
        'Artist': artist.name || '',
        'Priority': artist.tracking_label || '',
        'Global Building': formatNumber(artist.global_building_week_streams),
        'Global Building %': formatPercent(artist.global_building_week_change_percent),
        'Global Last Week': formatNumber(artist.global_last_complete_week_streams),
        'Global % Change': formatPercent(artist.global_week_over_week_change_percent),
        'US %': formatPercent(artist.us_streams_percent)
      }));

      // Artist Aggregated sheet
      const aggregatedData = (aggregatedRes.data.data || []).map(agg => ({
        'Artist': agg.name || '',
        'Songs': formatNumber(agg.song_count),
        'Global Building': formatNumber(agg.global_building_week_streams),
        'Global Building %': formatPercent(agg.global_building_week_change_percent),
        'Global Last Week': formatNumber(agg.global_last_complete_week_streams),
        'Global % Change': formatPercent(agg.global_week_over_week_change_percent),
        'US %': formatPercent(agg.us_streams_percent)
      }));

      // Combined sheet: Artists first, then Albums for artists not in Artists,
      // then Artist Aggregated for artists not in Artists or Albums.
      const normalizeName = (name) => (name || '').trim().toLowerCase();
      const artistNamesSet = new Set(
        (artistsRes.data.data || []).map(a => normalizeName(a.name))
      );
      const albumArtistNamesSet = new Set(
        (albumsRes.data.data || [])
          .map(a => normalizeName(a.artist))
          .filter(Boolean)
      );

      const combinedArtistRows = (artistsRes.data.data || []).map(artist => ({
        'Source': 'Artist',
        'Artist': artist.name || '',
        'Album': '',
        'Priority': artist.tracking_label || '',
        'Global Building': formatNumber(artist.global_building_week_streams),
        'Global Building %': formatPercent(artist.global_building_week_change_percent),
        'Global Last Week': formatNumber(artist.global_last_complete_week_streams),
        'Global % Change': formatPercent(artist.global_week_over_week_change_percent),
        'US %': formatPercent(artist.us_streams_percent)
      }));

      const combinedAlbumRows = (albumsRes.data.data || [])
        .filter(album => !artistNamesSet.has(normalizeName(album.artist)))
        .map(album => ({
          'Source': 'Album',
          'Artist': album.artist || '',
          'Album': album.title || '',
          'Priority': album.tracking_label || '',
          'Global Building': formatNumber(album.global_building_week_streams),
          'Global Building %': formatPercent(album.global_building_week_change_percent),
          'Global Last Week': formatNumber(album.global_last_complete_week_streams),
          'Global % Change': formatPercent(album.global_week_over_week_change_percent),
          'US %': formatPercent(album.us_streams_percent)
        }));

      const aggregatedArtistRows = (aggregatedRes.data.data || [])
        .filter(agg => {
          const key = normalizeName(agg.name);
          return !artistNamesSet.has(key) && !albumArtistNamesSet.has(key);
        })
        .map(agg => ({
          'Source': 'Artist Aggregated',
          'Artist': agg.name || '',
          'Album': '',
          'Priority': '',
          'Global Building': formatNumber(agg.global_building_week_streams),
          'Global Building %': formatPercent(agg.global_building_week_change_percent),
          'Global Last Week': formatNumber(agg.global_last_complete_week_streams),
          'Global % Change': formatPercent(agg.global_week_over_week_change_percent),
          'US %': formatPercent(agg.us_streams_percent)
        }));

      // The backend's artist-aggregated endpoint excludes artists with only 1 song.
      // Fill the gap from song-summaries: any unsigned artist not yet covered
      // gets their single song's metrics surfaced as an aggregated row.
      const aggregatedNamesSet = new Set(
        aggregatedArtistRows.map(r => normalizeName(r.Artist))
      );
      const singleSongArtistRows = [];
      const seenSingleSongArtists = new Set();
      (songsRes.data.data || []).forEach(song => {
        const key = normalizeName(song.artist);
        if (!key) return;
        if (artistNamesSet.has(key)) return;
        if (albumArtistNamesSet.has(key)) return;
        if (aggregatedNamesSet.has(key)) return;
        if (seenSingleSongArtists.has(key)) return;
        seenSingleSongArtists.add(key);
        singleSongArtistRows.push({
          'Source': 'Artist Aggregated',
          'Artist': song.artist || '',
          'Album': '',
          'Priority': '',
          'Global Building': formatNumber(song.global_building_week_streams),
          'Global Building %': formatPercent(song.global_building_week_change_percent),
          'Global Last Week': formatNumber(song.global_last_complete_week_streams),
          'Global % Change': formatPercent(song.global_week_over_week_change_percent),
          'US %': formatPercent(song.us_streams_percent)
        });
      });

      const combinedData = [
        ...combinedArtistRows,
        ...combinedAlbumRows,
        ...aggregatedArtistRows,
        ...singleSongArtistRows
      ];

      // Create workbook with multiple sheets
      const workbook = XLSX.utils.book_new();

      const songsSheet = XLSX.utils.json_to_sheet(songsData);
      const albumsSheet = XLSX.utils.json_to_sheet(albumsData);
      const artistsSheet = XLSX.utils.json_to_sheet(artistsData);
      const aggregatedSheet = XLSX.utils.json_to_sheet(aggregatedData);
      const combinedSheet = XLSX.utils.json_to_sheet(combinedData);

      // Set column widths for better readability
      const songAlbumColWidths = [
        { wch: 30 },  // Artist
        { wch: 40 },  // Song/Album
        { wch: 15 },  // Priority
        { wch: 18 },  // Global Building
        { wch: 18 },  // Global Building %
        { wch: 18 },  // Global Last Week
        { wch: 18 },  // Global % Change
        { wch: 10 }   // US %
      ];

      const artistColWidths = [
        { wch: 30 },  // Artist
        { wch: 15 },  // Priority
        { wch: 18 },  // Global Building
        { wch: 18 },  // Global Building %
        { wch: 18 },  // Global Last Week
        { wch: 18 },  // Global % Change
        { wch: 10 }   // US %
      ];

      const aggregatedColWidths = [
        { wch: 30 },  // Artist
        { wch: 10 },  // Songs
        { wch: 18 },  // Global Building
        { wch: 18 },  // Global Building %
        { wch: 18 },  // Global Last Week
        { wch: 18 },  // Global % Change
        { wch: 10 }   // US %
      ];

      const combinedColWidths = [
        { wch: 18 },  // Source
        { wch: 30 },  // Artist
        { wch: 40 },  // Album
        { wch: 15 },  // Priority
        { wch: 18 },  // Global Building
        { wch: 18 },  // Global Building %
        { wch: 18 },  // Global Last Week
        { wch: 18 },  // Global % Change
        { wch: 10 }   // US %
      ];

      songsSheet['!cols'] = songAlbumColWidths;
      albumsSheet['!cols'] = songAlbumColWidths;
      artistsSheet['!cols'] = artistColWidths;
      aggregatedSheet['!cols'] = aggregatedColWidths;
      combinedSheet['!cols'] = combinedColWidths;

      XLSX.utils.book_append_sheet(workbook, songsSheet, 'Songs');
      XLSX.utils.book_append_sheet(workbook, albumsSheet, 'Albums');
      XLSX.utils.book_append_sheet(workbook, artistsSheet, 'Artists');
      XLSX.utils.book_append_sheet(workbook, aggregatedSheet, 'Artist Aggregated');
      XLSX.utils.book_append_sheet(workbook, combinedSheet, 'Combined');

      // Generate and download file
      const fileName = `unsigned_analytics_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.dismiss(toastId);
      toast.success('Export complete', `Downloaded ${fileName}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.dismiss(toastId);
      toast.error('Export failed', 'Could not export data to Excel');
    } finally {
      setIsExporting(false);
    }
  };

  // ---- Agenda tab export ------------------------------------------------
  // The Agenda tab exports one row per artist, grouped into the four Agenda
  // sections, with a "Lead" column = the Set By user. Two output formats:
  // a styled HTML table (to copy) and an Excel sheet.

  // Section order + display headings, keyed by the tracking_label value.
  // Only the NEW section tints the artist-name cell (blue); the others are plain.
  const AGENDA_SECTIONS = [
    { label: 'Agenda - Legal', heading: 'LEGAL', color: null },
    { label: 'Agenda - Priorities', heading: 'PRIORITIES', color: null },
    { label: 'Agenda - New', heading: 'NEW', color: '#3b6ea5' }, // blue
    { label: 'Agenda - Tracking', heading: 'TRACKING', color: null },
  ];

  // Stat columns shown after Artist + Lead, with the exact titles requested.
  const AGENDA_STAT_COLS = [
    { key: 'global_building_week_streams', title: 'Global Building', kind: 'int' },
    { key: 'global_building_week_change_percent', title: 'Global Building %', kind: 'pct' },
    { key: 'global_last_complete_week_streams', title: 'Global Last Week', kind: 'int' },
    { key: 'global_week_over_week_change_percent', title: 'Global % Change', kind: 'pct' },
    { key: 'us_streams_percent', title: 'US %', kind: 'pct' },
  ];

  const leadName = (row) => {
    const email = row?.updated_by?.email || '';
    return email ? String(email).split('@')[0] : '';
  };

  const fmtInt = (n) => (n === null || n === undefined ? '' : Number(n).toLocaleString('en-US'));
  const fmtPct = (n) => (n === null || n === undefined ? '' : `${Math.round(Number(n))}%`);

  // Fetch all agenda rows and bucket them by section (order preserved).
  const fetchAgendaGrouped = async () => {
    const res = await axios.get(`${API_BASE_URL}/unsigned-agenda`, {
      params: { show_archived: showArchived, per_page: 10000, lag_days: lagDays,
                sort_by: 'agenda_label', sort_order: 'asc' }
    });
    const rows = res.data.data || [];
    return AGENDA_SECTIONS.map(section => ({
      ...section,
      rows: rows.filter(r => r.tracking_label === section.label),
    }));
  };

  const buildAgendaHtml = (grouped) => {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const posBg = 'background:#1e3a24;color:#7ee08a;';
    const negBg = 'background:#4a2222;color:#f08a8a;';
    const th = 'padding:8px 12px;font-weight:700;color:#e8edf2;text-align:center;background:#24455f;border:1px solid #0d1b26;';
    const cellBase = 'padding:6px 12px;border:1px solid #0d1b26;color:#e8edf2;';
    const colCount = 2 + AGENDA_STAT_COLS.length;

    const statCell = (r, col) => {
      const val = r[col.key];
      if (col.kind === 'pct') {
        const style = val == null ? '' : (val >= 0 ? posBg : negBg);
        return `<td style="${cellBase}text-align:right;font-weight:700;${style}">${esc(fmtPct(val))}</td>`;
      }
      return `<td style="${cellBase}text-align:right;font-variant-numeric:tabular-nums;">${esc(fmtInt(val))}</td>`;
    };

    let body = '';
    grouped.forEach(section => {
      if (!section.rows.length) return;
      // Section header row spanning all columns
      body += `<tr><td colspan="${colCount}" style="${cellBase}background:#111;color:#fff;font-weight:700;text-align:center;">${esc(section.heading)}</td></tr>`;
      section.rows.forEach(r => {
        // Only the NEW section tints the artist cell; others stay neutral.
        const artistBg = section.color ? `background:${section.color};` : 'background:#2a2a2a;';
        body += '<tr>'
          + `<td style="${cellBase}${artistBg}font-weight:700;">${esc(r.name)}</td>`
          + `<td style="${cellBase}text-align:center;">${esc(leadName(r))}</td>`
          + AGENDA_STAT_COLS.map(col => statCell(r, col)).join('')
          + '</tr>';
      });
    });

    const html =
      `<table style="border-collapse:collapse;background:#181818;font-family:Arial,Helvetica,sans-serif;font-size:14px;">`
      + `<thead><tr>`
      + `<th style="${th}">Artist</th>`
      + `<th style="${th}">Lead</th>`
      + AGENDA_STAT_COLS.map(col => `<th style="${th}">${esc(col.title)}</th>`).join('')
      + `</tr></thead><tbody>${body}</tbody></table>`;

    // Plain-text fallback (tab-separated) for the clipboard's text/plain flavor.
    const headerTitles = ['Artist', 'Lead', ...AGENDA_STAT_COLS.map(c => c.title)];
    let plain = headerTitles.join('\t') + '\n';
    grouped.forEach(section => {
      if (!section.rows.length) return;
      plain += `${section.heading}\n`;
      section.rows.forEach(r => {
        const cells = [r.name, leadName(r), ...AGENDA_STAT_COLS.map(c =>
          c.kind === 'pct' ? fmtPct(r[c.key]) : fmtInt(r[c.key]))];
        plain += cells.join('\t') + '\n';
      });
    });

    return { html, plain };
  };

  const handleAgendaHtmlExport = async () => {
    setShowAgendaExportModal(false);
    setIsExporting(true);
    const toastId = toast.loading('Building table...', 'Fetching agenda data');
    try {
      const grouped = await fetchAgendaGrouped();
      setAgendaHtml(buildAgendaHtml(grouped));
      setAgendaHtmlCopied(false);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Error building agenda HTML:', error);
      toast.dismiss(toastId);
      toast.error('Export failed', 'Could not build the agenda table');
    } finally {
      setIsExporting(false);
    }
  };

  const copyAgendaHtml = async () => {
    if (!agendaHtml) return;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([agendaHtml.html], { type: 'text/html' }),
            'text/plain': new Blob([agendaHtml.plain], { type: 'text/plain' }),
          })
        ]);
      } else {
        await navigator.clipboard.writeText(agendaHtml.plain);
      }
      setAgendaHtmlCopied(true);
      setTimeout(() => setAgendaHtmlCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Copy failed', 'Select the table and copy manually');
    }
  };

  const handleAgendaExcelExport = async () => {
    setShowAgendaExportModal(false);
    setIsExporting(true);
    const toastId = toast.loading('Exporting...', 'Fetching agenda data');
    try {
      const grouped = await fetchAgendaGrouped();

      // Colored Excel via ExcelJS (the xlsx package can't write cell fills).
      // Colors mirror the HTML export. ARGB = 'FF' alpha + RRGGBB.
      const fill = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + hex } });
      const font = (hex, bold) => ({ color: { argb: 'FF' + hex }, bold: !!bold });
      const thin = { style: 'thin', color: { argb: 'FF0D1B26' } };
      const border = { top: thin, left: thin, bottom: thin, right: thin };
      const colCount = 2 + AGENDA_STAT_COLS.length;

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Agenda');
      ws.columns = [{ width: 30 }, { width: 16 }, ...AGENDA_STAT_COLS.map(() => ({ width: 16 }))];

      // Header row
      const headerTitles = ['Artist', 'Lead', ...AGENDA_STAT_COLS.map(c => c.title)];
      const headerRow = ws.addRow(headerTitles);
      headerRow.eachCell(cell => {
        cell.fill = fill('24455F');
        cell.font = font('E8EDF2', true);
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = border;
      });

      grouped.forEach(section => {
        if (!section.rows.length) return;
        // Section header row, merged across all columns
        const sh = ws.addRow([section.heading]);
        ws.mergeCells(sh.number, 1, sh.number, colCount);
        for (let c = 1; c <= colCount; c++) {
          const cell = sh.getCell(c);
          cell.fill = fill('111111');
          cell.font = font('FFFFFF', true);
          cell.alignment = { horizontal: 'center' };
          cell.border = border;
        }

        section.rows.forEach(r => {
          const values = [r.name || '', leadName(r)];
          AGENDA_STAT_COLS.forEach(c => {
            const v = r[c.key];
            values.push(v == null ? 0 : Number(c.kind === 'pct' ? Number(v).toFixed(2) : v));
          });
          const row = ws.addRow(values);
          row.eachCell(cell => { cell.border = border; });

          // Artist cell: NEW = blue, others = neutral grey; white bold text.
          const artist = row.getCell(1);
          artist.fill = fill(section.color ? section.color.replace('#', '') : '2A2A2A');
          artist.font = font('E8EDF2', true);

          // Lead cell
          const lead = row.getCell(2);
          lead.fill = fill('181818');
          lead.font = font('E8EDF2');
          lead.alignment = { horizontal: 'center' };

          // Stat cells
          AGENDA_STAT_COLS.forEach((c, idx) => {
            const cell = row.getCell(3 + idx);
            cell.alignment = { horizontal: 'right' };
            if (c.kind === 'pct') {
              const v = r[c.key];
              cell.numFmt = '0"%"';
              if (v == null) { cell.fill = fill('181818'); cell.font = font('E8EDF2'); }
              else if (v >= 0) { cell.fill = fill('1E3A24'); cell.font = font('7EE08A', true); }
              else { cell.fill = fill('4A2222'); cell.font = font('F08A8A', true); }
            } else {
              cell.numFmt = '#,##0';
              cell.fill = fill('181818');
              cell.font = font('E8EDF2');
            }
          });
        });
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `unsigned_agenda_${new Date().toISOString().split('T')[0]}.xlsx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      toast.dismiss(toastId);
      toast.success('Export complete', `Downloaded ${fileName}`);
    } catch (error) {
      console.error('Error exporting agenda to Excel:', error);
      toast.dismiss(toastId);
      toast.error('Export failed', 'Could not export agenda data');
    } finally {
      setIsExporting(false);
    }
  };

  // Add songs mutation (bulk) - closes modal immediately and processes in background
  const addSongsMutation = useMutation(
    async ({ songIds, trackingLabel }) => {
      const response = await axios.post(`${API_BASE_URL}/add-unsigned-songs-bulk`, {
        luminate_ids: songIds,
        tracking_label: trackingLabel || undefined,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Refetch the data to update the table
        queryClient.invalidateQueries(['unsigned-songs']);
        const successCount = data.successful_count || 0;
        const failedCount = data.failed_count || 0;

        if (failedCount > 0) {
          toast.error(
            'Some songs failed to add',
            `${successCount} added, ${failedCount} failed`
          );
        } else {
          toast.success(
            'Songs added successfully',
            `${successCount} song(s) added to unsigned songs`
          );
        }
      },
      onError: (error) => {
        console.error('Error adding songs:', error);
        const errorMessage = error.response?.data?.error || 'Error adding songs. Please try again.';
        toast.error('Failed to add songs', errorMessage);
      }
    }
  );

  const resetAddModal = () => {
    setShowAddModal(false);
    setAddMode(null);
    setSearchStep(1);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSongs([]);
    setIsSearching(false);
    setIsAdding(false);
    setSongIdInput('');
    setAddSongLabel('');
  };

  const handleSearchSongs = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/search-songs`, {
        query: searchQuery
      });
      setSearchResults(response.data.results || []);
      setSearchStep(2);
    } catch (error) {
      console.error('Error searching songs:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddSongs = async () => {
    // Capture the chosen Priority before resetAddModal() clears it.
    const trackingLabel = addSongLabel;
    if (addMode === 'id') {
      if (!songIdInput.trim()) return;
      const songId = songIdInput.trim();
      // Close modal immediately and show loading toast
      resetAddModal();
      const toastId = toast.loading('Adding song...', 'Processing in background');
      addSongsMutation.mutate({ songIds: [songId], trackingLabel }, {
        onSettled: () => toast.dismiss(toastId)
      });
    } else {
      if (selectedSongs.length === 0) return;
      const songsToAdd = [...selectedSongs];
      // Close modal immediately and show loading toast
      resetAddModal();
      const toastId = toast.loading('Adding songs...', `Processing ${songsToAdd.length} song(s) in background`);
      addSongsMutation.mutate({ songIds: songsToAdd, trackingLabel }, {
        onSettled: () => toast.dismiss(toastId)
      });
    }
  };

  const toggleSongSelection = (songId) => {
    setSelectedSongs(prev => {
      if (prev.includes(songId)) {
        return prev.filter(id => id !== songId);
      }
      return [...prev, songId];
    });
  };

  const selectAllSongs = () => {
    if (selectedSongs.length === searchResults.length) {
      setSelectedSongs([]);
    } else {
      setSelectedSongs(searchResults.map(song => song.id));
    }
  };

  // Add artist mutation - closes modal immediately and processes in background
  const addArtistMutation = useMutation(
    async (artist) => {
      const response = await axios.post(`${API_BASE_URL}/add-unsigned-artist`, {
        luminate_artist_id: artist.id,
        // Name is optional now — the server resolves it from Luminate when the
        // add-by-ID flow doesn't supply one.
        artist_name: artist.name || undefined,
        tracking_label: artist.trackingLabel || undefined,
      });
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['unsigned-artists']);
        toast.success('Artist added successfully', `${variables.name || 'Artist'} added to unsigned artists`);
      },
      onError: (error, variables) => {
        console.error('Error adding artist:', error);
        const errorMessage = error.response?.data?.error || 'Error adding artist. Please try again.';
        toast.error(`Failed to add ${variables.name || 'artist'}`, errorMessage);
      }
    }
  );

  const resetAddArtistModal = () => {
    setShowAddArtistModal(false);
    setAddArtistMode(null);
    setAddArtistStep(1);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedArtist(null);
    setIsAdding(false);
    setArtistIdInput('');
    setArtistNameInput('');
    setAddArtistLabel('');
  };

  const handleSearchArtists = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/search-artists`, {
        artist_name: searchQuery  // Changed from query to artist_name
      });
      setSearchResults(response.data.results || []);
      setAddArtistStep(2);
    } catch (error) {
      console.error('Error searching artists:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddArtist = async () => {
    // Capture the chosen Priority before resetAddArtistModal() clears it.
    const trackingLabel = addArtistLabel;
    if (addArtistMode === 'id') {
      if (!artistIdInput.trim()) return;
      // Name is optional — resolved server-side from Luminate when omitted.
      const artistData = {
        id: artistIdInput.trim(),
        name: artistNameInput.trim() || undefined,
        trackingLabel,
      };
      // Close modal immediately and show loading toast
      resetAddArtistModal();
      const toastId = toast.loading('Adding artist...', `Processing ${artistData.name || 'artist'} in background`);
      addArtistMutation.mutate(artistData, {
        onSettled: () => toast.dismiss(toastId)
      });
    } else {
      if (!selectedArtist) return;
      const artistData = { ...selectedArtist, trackingLabel };
      // Close modal immediately and show loading toast
      resetAddArtistModal();
      const toastId = toast.loading('Adding artist...', `Processing ${artistData.name} in background`);
      addArtistMutation.mutate(artistData, {
        onSettled: () => toast.dismiss(toastId)
      });
    }
  };


  // Define columns for the song table
  const songColumns = [
    { key: 'artist', label: 'Artist', sortable: true },
    { key: 'title', label: 'Song', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true },
    ...(showAdvancedAnalytics ? [
      { key: 'weeks_grown_over_0_pct', label: 'Weeks Grown >0%', sortable: true, align: 'right' },
      { key: 'weeks_grown_over_neg5_pct', label: 'Weeks Grown >-5%', sortable: true, align: 'right' },
      { key: 'is_peak_daily_30days', label: 'Peak Daily 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_daily_180days', label: 'Peak Daily 180d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_30days', label: 'Peak Weekly 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_180days', label:'Peak Weekly 180d', sortable: true, align: 'center', isBoolean: true }
    ] : []),
    { key: 'tracked_date', label: 'Tracked', sortable: true, align: 'right', isDate: true }
  ];

  // Artist columns definition
  const artistColumns = [
    { key: 'name', label: 'Artist', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true },
    ...(showAdvancedAnalytics ? [
      { key: 'weeks_grown_over_0_pct', label: 'Weeks Grown >0%', sortable: true, align: 'right' },
      { key: 'weeks_grown_over_neg5_pct', label: 'Weeks Grown >-5%', sortable: true, align: 'right' },
      { key: 'is_peak_daily_30days', label: 'Peak Daily 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_daily_180days', label: 'Peak Daily 180d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_30days', label: 'Peak Weekly 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_180days', label:'Peak Weekly 180d', sortable: true, align: 'center', isBoolean: true }
    ] : []),
    { key: 'tracked_date', label: 'Tracked', sortable: true, align: 'right', isDate: true }
  ];

  // Artist Aggregated columns definition (aggregates song data by artist name)
  const artistAggregatedColumns = [
    { key: 'name', label: 'Artist', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'song_count', label: 'Songs', sortable: true, align: 'right' },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true }
  ];

  // Agenda columns definition — like the "Combined" export (one row per artist)
  // but showing Priority + Set By and stream data (no Source / Album columns).
  const agendaColumns = [
    { key: 'name', label: 'Artist', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true }
  ];

  // Album columns definition
  const albumColumns = [
    { key: 'artist', label: 'Artist', sortable: true },
    { key: 'title', label: 'Album', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true },
    ...(showAdvancedAnalytics ? [
      { key: 'weeks_grown_over_0_pct', label: 'Weeks Grown >0%', sortable: true, align: 'right' },
      { key: 'weeks_grown_over_neg5_pct', label: 'Weeks Grown >-5%', sortable: true, align: 'right' },
      { key: 'is_peak_daily_30days', label: 'Peak Daily 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_daily_180days', label: 'Peak Daily 180d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_30days', label: 'Peak Weekly 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_180days', label:'Peak Weekly 180d', sortable: true, align: 'center', isBoolean: true }
    ] : []),
    { key: 'tracked_date', label: 'Tracked', sortable: true, align: 'right', isDate: true }
  ];

  // Render expanded content for songs
  const renderExpandedContent = (item, type, metrics) => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <select
              value={timeRange}
              onChange={e => setTimeRange(Number(e.target.value))}
              className="w-1/2 px-4 py-2 border rounded-lg text-sm"
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last year</option>
            </select>

            <select
              value={selectedRegion}
              onChange={e => setSelectedRegion(e.target.value)}
              className="w-1/2 px-4 py-2 border rounded-lg text-sm"
            >
              <option value="global">Global</option>
              <option value="us">US</option>
            </select>
          </div>

          <div className="relative">
            <button
              onClick={() => setIsMetricsOpen(!isMetricsOpen)}
              className="w-full px-4 py-2 border rounded-lg text-sm text-blue-600 bg-white flex justify-between items-center"
            >
              {selectedMetricLabel}
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </button>
            
            {isMetricsOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
                {metricOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => toggleMetric(option.id)}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      selectedMetrics.includes(option.id) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                    {selectedMetrics.includes(option.id) && (
                      <CheckIcon className="h-5 w-5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="h-[250px] sm:h-[400px] w-full">
          {metrics && (
            <MetricsChart
              dates={metrics.dates}
              metrics={metrics.metrics}
              selectedMetrics={selectedMetrics}
            />
          )}
        </div>
      </div>
    );
  };

  // Source selector shown above every Unsigned view.
  const sourceSelector = (
    <div className="flex flex-wrap items-center gap-1 mb-3 sm:mb-4">
      {availableSources.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setActiveSource(s.id)}
          aria-pressed={activeSource === s.id}
          className={`rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium transition ${
            activeSource === s.id
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  // Snoop views replace the streaming body entirely. They own their own header,
  // filters and tables, so we return early rather than threading them through
  // the streaming layout below.
  if (activeSource !== 'streaming') {
    return (
      <div className="container mx-auto px-1 pt-1 pb-8 sm:py-4">
        {sourceSelector}
        {SOURCE_VIEWS[activeSource] || null}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-1 pt-1 pb-8 sm:py-4">
      {sourceSelector}
      <div className="flex flex-row justify-between items-start sm:items-center gap-3 mb-3 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Unsigned Analytics</h1>
          {(maxDates.weekly_date || maxDates.daily_date) && (
            <p className="text-[11px] sm:text-sm text-gray-500 sm:text-gray-600 mt-0.5 sm:mt-1">
              <span className="sm:hidden">
                {maxDates.weekly_date && `Wk ${maxDates.weekly_date}`}
                {maxDates.weekly_date && maxDates.daily_date && ' · '}
                {maxDates.daily_date && `Day ${maxDates.daily_date}`}
              </span>
              <span className="hidden sm:inline">
                {maxDates.weekly_date && <>Week Ending: {maxDates.weekly_date}<br /></>}
                {maxDates.daily_date && <>Daily Ending: {maxDates.daily_date}</>}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex flex-row items-center gap-2">
            {['songs', 'albums', 'artists'].includes(activeTab) && (
              <button
                onClick={() => {
                  setChangeStatusMode((prev) => {
                    const next = !prev;
                    if (!next) {
                      setSelectedSongIds([]);
                      setSelectedAlbumIds([]);
                      setSelectedArtistIds([]);
                    }
                    return next;
                  });
                }}
                className={`font-medium text-sm py-2 px-3 sm:px-4 rounded-lg shadow-sm hover:shadow transition whitespace-nowrap ${
                  changeStatusMode
                    ? 'bg-gray-700 hover:bg-gray-800 text-white'
                    : 'bg-white hover:bg-gray-50 text-gray-700 ring-1 ring-gray-300'
                }`}
              >
                {changeStatusMode ? 'Cancel' : <><span className="sm:hidden">Status</span><span className="hidden sm:inline">Change Status</span></>}
              </button>
            )}
            <button
              onClick={() => activeTab === 'agenda' ? setShowAgendaExportModal(true) : handleExportExcel()}
              disabled={isExporting}
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm py-2 px-3 sm:px-4 rounded-lg shadow-sm hover:shadow transition whitespace-nowrap disabled:bg-gray-300 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {isExporting ? '…' : 'Export'}
            </button>
          </div>

          {/* My Dashboard toggle — persists across tabs. Filters every table to
              rows the current user is the "Set by" for. */}
          <button
            type="button"
            onClick={() => setMyDashboard((v) => !v)}
            disabled={!user?.id}
            aria-pressed={myDashboard}
            className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>My Dashboard</span>
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                myDashboard ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  myDashboard ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      <div className="sm:border-b sm:border-gray-200 mb-4 sm:mb-6">
        <div className="flex flex-row items-center justify-between gap-2 sm:gap-3">
          {/* Mobile: compact dropdown selector. Desktop: tab bar. */}
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="sm:hidden shrink-0 w-auto px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
          >
            {TABS.map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}</option>
            ))}
          </select>
          <nav className="hidden sm:flex -mb-px gap-8">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-2 sm:gap-4 sm:pb-4">
            {/* Lag Toggle */}
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
              <span><span className="sm:hidden">Lag:</span><span className="hidden sm:inline">Building Week Lag:</span></span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setLagDays('2')}
                  className={`px-2.5 sm:px-3 py-1 rounded-lg text-xs font-medium transition ${
                    lagDays === '2'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <span className="sm:hidden">2d</span><span className="hidden sm:inline">2 Days</span>
                </button>
                <button
                  onClick={() => setLagDays('3')}
                  className={`px-2.5 sm:px-3 py-1 rounded-lg text-xs font-medium transition ${
                    lagDays === '3'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <span className="sm:hidden">3d</span><span className="hidden sm:inline">3 Days</span>
                </button>
              </div>
            </div>

            {/* Advanced Analytics Toggle */}
            <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 whitespace-nowrap">
              <input
                type="checkbox"
                checked={showAdvancedAnalytics}
                onChange={(e) => setShowAdvancedAnalytics(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="sm:hidden">Advanced</span>
              <span className="hidden sm:inline">Advanced Analytics</span>
            </label>
          </div>
        </div>
      </div>

      {/* Bulk action bar — only shown while Change Status mode is active. */}
      {changeStatusMode && ['songs', 'albums', 'artists'].includes(activeTab) && currentSelection.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl shadow-sm px-4 py-3 mb-3 flex items-center justify-between gap-4">
          <div className="text-sm text-blue-900">
            <span className="font-medium">{currentSelection.length}</span>{' '}
            {activeTab === 'songs' ? 'song' : activeTab === 'albums' ? 'album' : 'artist'}
            {currentSelection.length === 1 ? '' : 's'} selected
          </div>
          <div className="flex items-center gap-2">
            <select
              value={bulkLabel}
              onChange={(e) => setBulkLabel(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
            >
              <option value="">Set label to…</option>
              {trackingLabelOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={applyBulkLabel}
              disabled={!bulkLabel}
              className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearCurrentSelection}
              className="text-sm px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {/* Full-bleed on mobile: cancel the page (px-1) + main (px-1) padding so
          the table stretches edge-to-edge; normal flow at sm+. */}
      <div className="-mx-2 sm:mx-0">
        {activeTab === 'songs' ? (
          <div>
            <StreamingTable
              type="song"
              forcedTrackedBy={forcedTrackedBy}
              queryKey={['unsigned-songs', 'song']}
              apiEndpoint={`${API_BASE_URL}/song-summaries`}
              columns={songColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleSongRowClick}
              expandedRow={expandedSongRow}
              onTrackingLabelChange={(id, label) => handleTrackingLabelChange(id, label, 'song')}
              userOptions={unsignedUsers || []}
              onUpdatedByChange={(id, userId) => handleUpdatedByChange(id, userId, 'song')}
              editingTrackingLabel={editingTrackingLabel}
              setEditingTrackingLabel={setEditingTrackingLabel}
              trackingLabelOptions={trackingLabelOptions}
              renderExpandedContent={(item) => renderExpandedContent(item, 'song')}
              selectedRegion={selectedRegion}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddClick={() => setShowAddModal(true)}
              group="Unsigned"
              // audioOnly={audioOnly}
              lagDays={lagDays}
              columnFilters={songColumnFilters}
              onColumnFiltersChange={setSongColumnFilters}
              selectedIds={selectedSongIds}
              onSelectionChange={setSelectedSongIds}
              enableSelection={changeStatusMode}
            />
          </div>
        ) : activeTab === 'albums' ? (
          <div>
            <StreamingTable
              type="album"
              forcedTrackedBy={forcedTrackedBy}
              queryKey={['unsigned-albums', 'album']}
              apiEndpoint={`${API_BASE_URL}/unsigned-album-summaries`}
              columns={albumColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleAlbumRowClick}
              expandedRow={expandedAlbumRow}
              onTrackingLabelChange={(id, label) => handleTrackingLabelChange(id, label, 'album')}
              userOptions={unsignedUsers || []}
              onUpdatedByChange={(id, userId) => handleUpdatedByChange(id, userId, 'album')}
              editingTrackingLabel={editingTrackingLabel}
              setEditingTrackingLabel={setEditingTrackingLabel}
              trackingLabelOptions={trackingLabelOptions}
              renderExpandedContent={(item) => renderExpandedContent(item, 'album')}
              selectedRegion={selectedRegion}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddClick={() => setShowAddAlbumModal(true)}
              group="Unsigned"
              lagDays={lagDays}
              columnFilters={albumColumnFilters}
              onColumnFiltersChange={setAlbumColumnFilters}
              selectedIds={selectedAlbumIds}
              onSelectionChange={setSelectedAlbumIds}
              enableSelection={changeStatusMode}
            />
          </div>
        ) : activeTab === 'artists' ? (
          <div>
            <StreamingTable
              type="artist"
              forcedTrackedBy={forcedTrackedBy}
              queryKey={['unsigned-artists', 'artist']}
              apiEndpoint={`${API_BASE_URL}/unsigned-artist-summaries`}
              columns={artistColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleArtistRowClick}
              expandedRow={expandedArtistRow}
              onTrackingLabelChange={(id, label) => handleTrackingLabelChange(id, label, 'artist')}
              userOptions={unsignedUsers || []}
              onUpdatedByChange={(id, userId) => handleUpdatedByChange(id, userId, 'artist')}
              editingTrackingLabel={editingTrackingLabel}
              setEditingTrackingLabel={setEditingTrackingLabel}
              trackingLabelOptions={trackingLabelOptions}
              renderExpandedContent={(item) => renderExpandedContent(item, 'artist')}
              selectedRegion={selectedRegion}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddClick={() => setShowAddArtistModal(true)}
              group="Unsigned"
              // audioOnly={audioOnly}
              lagDays={lagDays}
              columnFilters={artistColumnFilters}
              onColumnFiltersChange={setArtistColumnFilters}
              selectedIds={selectedArtistIds}
              onSelectionChange={setSelectedArtistIds}
              enableSelection={changeStatusMode}
            />
          </div>
        ) : activeTab === 'artist-aggregated' ? (
          <div>
            <StreamingTable
              type="artist-aggregated"
              forcedTrackedBy={forcedTrackedBy}
              queryKey={['unsigned-artist-aggregated']}
              apiEndpoint={`${API_BASE_URL}/unsigned-artist-aggregated`}
              columns={artistAggregatedColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleAggregatedRowClick}
              expandedRow={expandedAggregatedRow}
              onTrackingLabelChange={(artistName, label) => handleAgendaTrackingLabelChange(artistName, label)}
              editingTrackingLabel={editingTrackingLabel}
              setEditingTrackingLabel={setEditingTrackingLabel}
              trackingLabelOptions={trackingLabelOptions}
              renderExpandedContent={(item) => renderExpandedContent(item, 'artist-aggregated')}
              selectedRegion={selectedRegion}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddClick={() => {}}
              group="Unsigned"
              lagDays={lagDays}
              hideAddButton={true}
              enableSelection={false}
              columnFilters={aggregatedColumnFilters}
              onColumnFiltersChange={setAggregatedColumnFilters}
              userOptions={unsignedUsers || []}
              onUpdatedByChange={(artistName, userId) => handleAggregatedUpdatedByChange(artistName, userId)}
            />
          </div>
        ) : (
          <div>
            <StreamingTable
              type="agenda"
              forcedTrackedBy={forcedTrackedBy}
              queryKey={['unsigned-agenda']}
              apiEndpoint={`${API_BASE_URL}/unsigned-agenda`}
              columns={agendaColumns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleAgendaRowClick}
              expandedRow={expandedAgendaRow}
              onTrackingLabelChange={(artistName, label) => handleAgendaTrackingLabelChange(artistName, label)}
              editingTrackingLabel={editingTrackingLabel}
              setEditingTrackingLabel={setEditingTrackingLabel}
              trackingLabelOptions={trackingLabelOptions}
              renderExpandedContent={(item) => renderExpandedContent(item, 'artist-aggregated')}
              selectedRegion={selectedRegion}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddClick={() => {}}
              group="Unsigned"
              lagDays={lagDays}
              hideAddButton={true}
              enableSelection={false}
              columnFilters={agendaColumnFilters}
              onColumnFiltersChange={setAgendaColumnFilters}
              userOptions={unsignedUsers || []}
              onUpdatedByChange={(artistName, userId) => handleAgendaUpdatedByChange(artistName, userId)}
            />
          </div>
        )}
      </div>

      {/* Add Song Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {!addMode ? 'Add Song' : addMode === 'id' ? 'Add Song by ID' : searchStep === 1 ? 'Search for a Song' : 'Select a Song'}
                </h3>
                <button
                  onClick={resetAddModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Mode Selection */}
              {!addMode && (
                <div className="space-y-3">
                  <button
                    onClick={() => setAddMode('id')}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Add by Luminate ID
                  </button>
                  <button
                    onClick={() => setAddMode('search')}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search for Song
                  </button>
                </div>
              )}

              {/* Add by ID */}
              {addMode === 'id' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Luminate Song ID
                    </label>
                    <input
                      type="text"
                      value={songIdInput}
                      onChange={(e) => setSongIdInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddSongs()}
                      placeholder="Paste Luminate ID..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isAdding}
                    />
                  </div>
                  {renderAddPrioritySelect(addSongLabel, setAddSongLabel)}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddMode(null)}
                      disabled={isAdding}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddSongs}
                      disabled={isAdding || !songIdInput.trim()}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Song
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: Search */}
              {addMode === 'search' && searchStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Song Title
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchSongs()}
                      placeholder="Enter song title to search..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSearching}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddMode(null)}
                      disabled={isSearching}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSearchSongs}
                      disabled={isSearching || !searchQuery.trim()}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSearching ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Searching...
                        </>
                      ) : (
                        'Search'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Select Songs */}
              {addMode === 'search' && searchStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSongs.length === searchResults.length && searchResults.length > 0}
                        onChange={selectAllSongs}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Select All ({selectedSongs.length} selected)
                    </label>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {searchResults.map((song) => (
                      <div
                        key={song.id}
                        onClick={() => toggleSongSelection(song.id)}
                        className={`p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-50 ${
                          selectedSongs.includes(song.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedSongs.includes(song.id)}
                            onChange={() => toggleSongSelection(song.id)}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <div className="font-medium">{song.display_artist_name} - {song.title}</div>
                            {song.isrc && (
                              <div className="text-xs text-gray-400">
                                ISRC: {song.isrc}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {renderAddPrioritySelect(addSongLabel, setAddSongLabel)}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSearchStep(1)}
                      disabled={isAdding}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddSongs}
                      disabled={isAdding || selectedSongs.length === 0}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add {selectedSongs.length} Song{selectedSongs.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Export {activeTab === 'songs' ? 'Songs' : 'Artists'}
                </h3>
                <button
                  onClick={() => {
                    setShowExportModal(false);
                    setExportType(activeTab);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export as CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agenda Export Chooser Modal */}
      {showAgendaExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Export Agenda</h3>
                <button
                  onClick={() => setShowAgendaExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleAgendaHtmlExport}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v3H4V5zm0 5h5v5H4v-5zm7 0h5v5h-5v-5z" clipRule="evenodd" />
                  </svg>
                  Copy as HTML Table
                </button>
                <button
                  onClick={handleAgendaExcelExport}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Excel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agenda HTML Preview Modal */}
      {agendaHtml && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-10 mx-auto p-5 border max-w-4xl w-full shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Agenda Table</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyAgendaHtml}
                  className={`px-4 py-2 rounded-md text-sm font-medium text-white transition ${
                    agendaHtmlCopied ? 'bg-green-600' : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {agendaHtmlCopied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => setAgendaHtml(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Preview below. Click Copy to place the styled table on your clipboard, then paste into email, Docs, or Slides.
            </p>
            <div className="overflow-auto max-h-[70vh] rounded" dangerouslySetInnerHTML={{ __html: agendaHtml.html }} />
          </div>
        </div>
      )}

      {/* Add Artist Modal */}
      {showAddArtistModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {!addArtistMode ? 'Add Artist' : addArtistMode === 'id' ? 'Add Artist by ID' : addArtistStep === 1 ? 'Search for an Artist' : 'Select an Artist'}
                </h3>
                <button
                  onClick={resetAddArtistModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Mode Selection */}
              {!addArtistMode && (
                <div className="space-y-3">
                  <button
                    onClick={() => setAddArtistMode('id')}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Add by Luminate ID
                  </button>
                  <button
                    onClick={() => setAddArtistMode('search')}
                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search for Artist
                  </button>
                </div>
              )}

              {/* Add by ID */}
              {addArtistMode === 'id' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Luminate Artist ID
                    </label>
                    <input
                      type="text"
                      value={artistIdInput}
                      onChange={(e) => setArtistIdInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddArtist()}
                      placeholder="Paste Luminate Artist ID..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isAdding}
                    />
                    <p className="mt-1 text-xs text-gray-500">The artist name is pulled from Luminate automatically.</p>
                  </div>
                  {renderAddPrioritySelect(addArtistLabel, setAddArtistLabel)}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddArtistMode(null)}
                      disabled={isAdding}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddArtist}
                      disabled={isAdding || !artistIdInput.trim()}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Artist
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: Search */}
              {addArtistMode === 'search' && addArtistStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Artist Name
                    </label>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchArtists()}
                      placeholder="Enter artist name to search..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSearching}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAddArtistMode(null)}
                      disabled={isSearching}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSearchArtists}
                      disabled={isSearching || !searchQuery.trim()}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSearching ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Searching...
                        </>
                      ) : (
                        'Search'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Select Artist */}
              {addArtistMode === 'search' && addArtistStep === 2 && (
                <div className="space-y-4">
                  <div className="max-h-60 overflow-y-auto">
                    {searchResults.map((artist) => (
                      <div
                        key={artist.id}
                        onClick={() => setSelectedArtist(artist)}
                        className={`p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-50 ${
                          selectedArtist?.id === artist.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="font-medium">{artist.name}</div>
                        {artist.id && (
                          <div className="text-xs text-gray-400">
                            ID: {artist.id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {renderAddPrioritySelect(addArtistLabel, setAddArtistLabel)}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setAddArtistStep(1);
                        setSelectedArtist(null);
                      }}
                      disabled={isAdding}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddArtist}
                      disabled={isAdding || !selectedArtist}
                      className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Artist
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Album Modal */}
      <AddAlbumModal
        isOpen={showAddAlbumModal}
        onClose={() => setShowAddAlbumModal(false)}
        group="Unsigned"
        queryKeyToInvalidate={['unsigned-albums']}
      />
    </div>
  );
} 